import { ApolloClient } from '@apollo/client';
import { Dispatcher } from '@thoughtspot/blink-context';
import { translate as t } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import { ChipBaseType } from '@thoughtspot/radiant-react/widgets/chip/chip-base';
import _ from 'lodash';
import { Subject } from 'rxjs';
import {
    Column,
    FalconDataType,
    PhraseType,
    RecognizedTokenType,
    SagePhrase,
    SageRequestFlag,
    SageToken,
    SageTokenInput,
} from '/@services/generated/graphql-types';
import { MetricsService } from '/@services/metrics-service/metrics-service';
import { UnderlyingDataColumn } from '../components/show-underlying-data/show-underlying-data-modal/show-underlying-data-column-list';

export interface SageMixpanelInfo {
    hasUnresolvedTokens: boolean;
    totalTokens: number;
}

/*
 * Several components in the sage stack pass along props to components lower in the stack. To avoid
 * redefining the same prop interface for these common props, we instead define them here and each
 * sage component extends this interface with its own set of props.
 */
export interface SageComponentBaseProps {
    /*
     * Services
     */
    // Service for metrics tracking
    metricsService: MetricsService;

    /*
     * Static visual settings
     */
    // Whether the sage bar should be focused upon first render
    isFocusedOnInit: boolean;
    // Text to display while the sage bar is empty and not in focus
    placeholderText: string;
    // Tooltip to display on the sage bar body (ie. empty space where there are no tokens)
    bodyTooltip?: string;
    // Whether the sage token context menu is enabled
    isTokenContextMenuEnabled?: boolean;

    /*
     * Sage request related
     */
    // How many data sources are selected
    nSelectedDataSources: number;
    // Whether we got a response to the very first sage request
    isInitialRequestComplete: boolean;
    // Current phrase data to display in the sage bar
    phrases: SagePhrase[];
    // Whether the phrases in the previous prop came from an external source. We use this to
    // determine whether to gate the data on a text match with the current token-bar contents
    // before being applied to the token-bar. If the phrase data came from an external source, we
    // accept it without any text matching requirement. If not, that means the phrase data came
    // from a regular sage request, so we make sure the phrase text matches the current text in the
    // token-bar. If no match, we ignore the new phrase data, since it means the user has made an
    // edit that is more recent than this phrase data.
    isPhraseDataUpdatedExternally: boolean;
    // Current completions data to display in the dropdown
    completions: BlinkCompletion[];
    // Whether more completions are available for the current query and caret position
    hasMoreCompletions: boolean;
    // Whether it is impossible to show any completions for the current state of the sage query at
    // the current caret position. This can be used to determine whether to show an empty state in
    // the completions dropdown, or to hide the dropdown altogether.
    completionsNotPossible: boolean;
    // Whether there was an error in the last sage response
    hasError: boolean;

    /*
     * Imperative handling event emitters
     */
    // Emitting on this subject will cause the sage bar to move the caret to the end of the token
    // at the specified index (it will be at the end of the token itself, not moved out into the
    // subsequent separator). Will focus the sage bar if it is currently blurred. Emitting -1 will
    // place the caret at the end of the query text (in the separator).
    placeCaretAfterTokenAtIdxSubject?: Subject<number>;
    // Emitting on this subject will cause the sage bar to blur and make a sage request with its
    // current contents (will update answer if required as a result)
    blurAndSubmitSearchSubject?: Subject<void>;

    /*
     * Callbacks
     */
    // Callback for when the focus state of the sage bar changes
    onFocusChanged: (isVisible: boolean) => void;
    // Callback for when we want to fetch more completions
    // TODO(Rifdhan) we can probably combine this with onTextOrCaretPositionChanged
    getMoreCompletions: (
        updatedTokens: SageTokenInput[],
        caretTokenIdx: number,
        charOffsetInCaretToken: number,
        sageRequestFlags?: SageRequestFlag[],
    ) => void;
    // Callback when the user submits the search (eg pressing enter, clicking the search button),
    // returns true if search performed, false otherwise (based on hashkey changes)
    onSearchSubmitted: () => boolean;

    /*
     * Search Assist related
     */
    // Data about the sage query required for metrics
    mixpanelSageData: SageMixpanelInfo; // TODO(Rifdhan) do we need this?

    /*
     * Others
     */
    // Data about the answer required for metrics
    getAnswerMixpanelProperties: () => any; // TODO(Utsav) add proper typing
    // Callback to dispatch an embed-related event
    dispatchEmbedEvent: Dispatcher;
}

const logger = create('Sage-Util');

export enum PhraseClassification {
    Unrecognized, // Red token
    Measure, // Green token
    Attribute, // Blue token
    Date, // Purple token
    Other, // Gray token
}

const PHRASE_CLASSIFICATION_BY_PHRASE_TYPE: {
    [key in PhraseType]: PhraseClassification;
} = {
    // Unrecognized
    [PhraseType.UndefinedPhrase]: PhraseClassification.Unrecognized,
    // Measure
    [PhraseType.AggregatedColumnPhrase]: PhraseClassification.Measure,
    [PhraseType.AggregatedAttributeValuePhrase]: PhraseClassification.Measure,
    [PhraseType.GrowthPhrase]: PhraseClassification.Measure,
    [PhraseType.OfPhrase]: PhraseClassification.Measure,
    [PhraseType.PercentageOfPhrase]: PhraseClassification.Measure,
    [PhraseType.VersusPhrase]: PhraseClassification.Measure,
    [PhraseType.VersusSubqueryPhrase]: PhraseClassification.Measure,
    // Attribute
    [PhraseType.GroupByColumnPhrase]: PhraseClassification.Attribute,
    // Filter or other
    [PhraseType.FilterPhrase]: PhraseClassification.Other,
    [PhraseType.FilteredCountPhrase]: PhraseClassification.Other,
    [PhraseType.GeofilterPhrase]: PhraseClassification.Other,
    [PhraseType.InFilterPhrase]: PhraseClassification.Other,
    [PhraseType.OfFilterPhrase]: PhraseClassification.Other,
    [PhraseType.InFilterSubqueryPhrase]: PhraseClassification.Other,
    [PhraseType.HavingPhrase]: PhraseClassification.Other,
    [PhraseType.PercentageOfFilterPhrase]: PhraseClassification.Other,
    [PhraseType.SortByPhrase]: PhraseClassification.Other,
    [PhraseType.TopBottomPhrase]: PhraseClassification.Other,
    [PhraseType.TopBottomFilterPhrase]: PhraseClassification.Other,
    [PhraseType.Begin]: PhraseClassification.Other,
    [PhraseType.End]: PhraseClassification.Other,
    [PhraseType.CalendarPhrase]: PhraseClassification.Other,
    [PhraseType.DelimiterPhrase]: PhraseClassification.Other,
    [PhraseType.FormulaPhrase]: PhraseClassification.Other,
    [PhraseType.ForEachPhrase]: PhraseClassification.Other,
    [PhraseType.OfSortByPhrase]: PhraseClassification.Other,
    [PhraseType.PercentageOfSortByPhrase]: PhraseClassification.Other,
    [PhraseType.PivotPhrase]: PhraseClassification.Other,
    [PhraseType.ShowColumnPhrase]: PhraseClassification.Other,
    [PhraseType.SkipTokenPhrase]: PhraseClassification.Other,
    [PhraseType.StopWordPhrase]: PhraseClassification.Other,
};

// TODO(Rifdhan) use GraphQL types
export enum BlinkCompletionType {
    Unrecognized,
    Measure,
    Attribute,
    Filter,
    DateBucket,
    // Constant data value, eg "red" or the number in "tax > 10"
    ConstantFromColumn,
    // Constant number not from any data column, eg the number in "top 10"
    ConstantNumber,
    Keyword,
    Operator,
    CustomCalendar,
    Folded,
}

// If the completion should be appended to the previous phrase, then its own phrase type is ignored
// TODO(Rifdhan) use GraphQL types
interface BlinkBaseCompletion {
    type: BlinkCompletionType;
    nPrefixTokens: number;
    nSuffixTokens: number;
    completionPhrases: SagePhrase[];
    shouldAppendToPreviousPhrase: boolean;
    completesCurrentPhrase: boolean;
    isDisabled: boolean;
}

export interface BlinkCompletion extends BlinkBaseCompletion {
    type: Exclude<BlinkCompletionType, BlinkCompletionType.Folded>;
    containsFormulaToken: boolean;
    containsCohortToken: boolean;
    isHistory: boolean;
    isSnyonym: boolean;
}

export interface BlinkFoldedCompletion extends BlinkBaseCompletion {
    type: BlinkCompletionType.Folded;
    nCompletions: number;
}

export type BlinkCompletionOrFoldedCompletion =
    | BlinkCompletion
    | BlinkFoldedCompletion;

/*
 * Represents the absolute offset of the caret in the set of tokens as a flattened string, and also
 * accounts for the difference between being inside a separator, or being at the edge of a token
 * but not in the separator. If the caret is inside a separator, the caret offset is recorded as
 * being at the end of the previous token.
 *
 * For example, if the token text is "rev date" (2 tokens):
 * - if the caret is at the end of "rev" and inside that token, the representation would be:
 *   { caretCharIdx: 3, isCaretInSeparator: false }
 * - if the caret is in the separator between "rev" and "date", the representation would be:
 *   { caretCharIdx: 3, isCaretInSeparator: true }
 * - if the caret is at the start of "date" and inside that token, the representation would be:
 *   { caretCharIdx: 4, isCaretInSeparator: false }
 */
export interface CaretPosition {
    caretCharIdx: number;
    isCaretInSeparator: boolean;
}

export function getClassificationForPhrase(
    phrase: SagePhrase,
): PhraseClassification {
    if (!phrase.isComplete || phrase.type === PhraseType.UndefinedPhrase) {
        return PhraseClassification.Unrecognized;
    }

    const phraseClassification =
        PHRASE_CLASSIFICATION_BY_PHRASE_TYPE[phrase.type];

    // Must manually detect dates because sage BE doesn't differentiate dates from attributes
    if (
        phraseClassification === PhraseClassification.Attribute &&
        phrase.tokens.some(
            token =>
                token.dataType === FalconDataType.Date ||
                token.dataType === FalconDataType.DateTime ||
                token.dataType === FalconDataType.Time,
        )
    ) {
        return PhraseClassification.Date;
    }

    return phraseClassification;
}

const CHIP_BASE_TYPE_BY_PHRASE_CLASSIFICATION: {
    [key in PhraseClassification]: ChipBaseType;
} = {
    [PhraseClassification.Unrecognized]: 'default',
    [PhraseClassification.Measure]: 'measure',
    [PhraseClassification.Attribute]: 'attribute',
    [PhraseClassification.Date]: 'date',
    [PhraseClassification.Other]: 'filter',
};

export function getChipBaseTypeForPhrase(phrase: SagePhrase): ChipBaseType {
    return CHIP_BASE_TYPE_BY_PHRASE_CLASSIFICATION[
        getClassificationForPhrase(phrase)
    ];
}

export function getTextForPhrases(phrases: SagePhrase[]): string {
    return phrases
        .map((phrase: SagePhrase) => {
            return phrase.tokens
                .map((token: SageToken) => {
                    return token.text + (token.hasSpaceAfter ? ' ' : '');
                })
                .join('');
        })
        .join('');
}

export function getTextForTokens(
    tokens: (SageToken | SageTokenInput)[],
): string {
    return tokens
        .map((token: SageToken | SageTokenInput) => {
            // If the token specifies trailingWhitespace, use that. If not, fall back to the older
            // hasSpaceAfter field instead. We need a bit of type checking to make this work.
            let trailingWhitespace: string;
            if (
                !_.isNil(
                    (token as SageToken | SageTokenInput).trailingWhitespace,
                )
            ) {
                trailingWhitespace = (token as SageToken | SageTokenInput)
                    .trailingWhitespace;
            } else {
                trailingWhitespace = token.hasSpaceAfter ? ' ' : '';
            }

            return token.text + trailingWhitespace;
        })
        .join('');
}

/*
 * Each phrase can contain tokens from different columns or even different data sources. We need to
 * ensure the tooltip displays the correct information for each token in the phrase, while also
 * keeping the tooltip simple for simple phrases, where all tokens are from the same column and
 * data source.
 *
 * Note: this is the legacy tooltipl; we have a new Knowledge Card added as part of Spotter. We fall
 * back to this tooltip only if the Spotter Knowledge Card is disabled.
 */
export function getTooltipForSagePhrase(sagePhrase: SagePhrase): string {
    const tooltipRows = [];

    // Add a row for the phrase type and whether it is a formula
    let typeString: string;
    if (sagePhrase.type === PhraseType.CalendarPhrase) {
        typeString = t('sageV2.tokenTooltip.calendarType');
    } else if (sagePhrase.type === PhraseType.GroupByColumnPhrase) {
        typeString = t('sageV2.tokenTooltip.attributeType');
    } else if (
        sagePhrase.type === PhraseType.AggregatedAttributeValuePhrase ||
        sagePhrase.type === PhraseType.AggregatedColumnPhrase ||
        sagePhrase.type === PhraseType.GrowthPhrase ||
        sagePhrase.type === PhraseType.OfPhrase ||
        sagePhrase.type === PhraseType.PercentageOfPhrase ||
        sagePhrase.type === PhraseType.VersusPhrase ||
        sagePhrase.type === PhraseType.VersusSubqueryPhrase
    ) {
        typeString = t('sageV2.tokenTooltip.measureType');
    } else {
        // TODO(Rifdhan) not all PhraseType.Other are actually filters, eg a sort phrase. I can't
        // think of a simple way to detect just filters though, so leaving this for now.
        typeString = t('sageV2.tokenTooltip.filterType');
    }
    if (
        typeString &&
        sagePhrase.tokens.some(
            (sageToken: SageToken) => sageToken.isFromFormula,
        )
    ) {
        typeString += ` ${t('sageV2.tokenTooltip.formulaTypeSuffix')}`;
    }
    if (
        typeString &&
        sagePhrase.tokens.some((sageToken: SageToken) => sageToken.isFromCohort)
    ) {
        typeString += ` ${t('sageV2.tokenTooltip.cohortTypeSuffix')}`;
    }
    if (typeString) {
        tooltipRows.push(typeString);
    }

    // Find all relevant source/column info from the tokens inside the given phrase. We filter out
    // tokens which have no source/column info, and tokens which do not have source/column info
    // that would make sense to the end user (eg keyword tokens).
    const tokenInfo = sagePhrase.tokens
        .filter((token: SageToken) => {
            // Skip tokens that don't have at least one of data source info or column info
            if (!token.source?.name && !token.column?.name) {
                return false;
            }

            // Keyword tokens will have a column name set to the keyword name, and no data source
            // info. We don't want to display this, so skip those tokens.
            // TODO(Rifdhan) can we get backend to not set this for keyword tokens?
            if (token.column?.name && !token.source?.name) {
                return false;
            }

            return true;
        })
        .map((token: SageToken) => {
            return {
                text: token.text,
                source: token.source?.name,
                column: token.column?.name,
            };
        });

    // If none of the tokens contained any relevant source/column info, we can return early
    if (tokenInfo.length === 0) {
        return tooltipRows.join('\n');
    }

    // For all tokens which provided source/column info, check if the source/column info was
    // identical. This will also be true if there was only 1 entry in the list.
    let allIdentical = true;
    for (let i = 1; i < tokenInfo.length; i++) {
        if (
            tokenInfo[i].source !== tokenInfo[0].source ||
            tokenInfo[i].column !== tokenInfo[0].column
        ) {
            // We found a token with different source/column info
            allIdentical = false;
            break;
        }
    }

    if (allIdentical) {
        // Add a single copy of the source/column info, since it is the same for all tokens
        if (tokenInfo[0].source) {
            tooltipRows.push(
                t('sageV2.tokenTooltip.sourceInfo', {
                    sourceName: tokenInfo[0].source,
                }),
            );
        }
        if (tokenInfo[0].column && tokenInfo[0].column !== tokenInfo[0].text) {
            tooltipRows.push(
                t('sageV2.tokenTooltip.columnInfo', {
                    columnName: tokenInfo[0].column,
                }),
            );
        }
    } else {
        // There were tokens with different source/column info, so we need to display separate
        // entries for each token with its respective info
        tokenInfo.forEach(info => {
            // Add a blank line to separate the sections, then add the token's text as a header,
            // followed by its source/column info
            tooltipRows.push('');
            tooltipRows.push(`${info.text}`);
            if (info.source) {
                tooltipRows.push(
                    t('sageV2.tokenTooltip.sourceInfo', {
                        sourceName: info.source,
                    }),
                );
            }
            if (info.column && info.column !== info.text) {
                tooltipRows.push(
                    t('sageV2.tokenTooltip.columnInfo', {
                        columnName: info.column,
                    }),
                );
            }
        });
    }

    return tooltipRows.join('\n');
}

/*
 * Return true if a column has synonym
 */
export function isSynonym(token: SageToken | SageTokenInput): boolean {
    return !_.isUndefined(
        (token.recognizedTokenJson as { [key: string]: any })?.synonymSource,
    );
}

/**
 * Returns `true` if any of the tokens is a cohort column
 *   but not from a cohort column.
 *
 * E.g.,
 * - `Discount set` is a cohort column.
 * - Completions shown for `Discount set = ` are from a cohort column.
 */
export const isAnyTokenACohort = (tokens: SageToken[]) =>
    tokens.some(
        token =>
            /**
             * `isFromCohort` value needs to be considered only with `SageToken`s.
             *
             * Whether or not a token is a cohort column is determined by comparing
             *   the token's text with the token's column name.
             */
            token.isFromCohort !== false && token.text === token.column?.name,
    );

/**
 * Returns `true` if any of the tokens is from a cohort column
 *   but not the cohort column itself.
 *
 * E.g.,
 * - `Discount set` is a cohort column.
 * - Completions shown for `Discount set = ` are from a cohort column.
 */
export const isAnyTokenFromACohort = (tokens: SageToken[]) =>
    tokens.some(
        token =>
            /**
             * `isFromCohort` value needs to be considered only with `SageToken`s.
             *
             * Whether or not a token is a cohort column is determined by comparing
             *   the token's text with the token's column name.
             */
            token.isFromCohort !== false && token.text !== token.column?.name,
    );

/**
 * Returns `true` if the completion is a cohort column
 *   but not from a cohort column.
 * - A completion can have multiple tokens and so can be both
 *     a cohort column and from a cohort column.
 *
 * E.g.,
 * - `Discount set` is a cohort column.
 * - Completions shown for `Discount set = ` are from a cohort column.
 * - `Discount set = > 12` is both.
 */
export const isCompletionACohort = (
    completion: BlinkCompletionOrFoldedCompletion,
) =>
    // TS Bug: Combined interfaces as a type don't pass validation.
    (completion as BlinkCompletion)?.containsCohortToken &&
    isAnyTokenACohort(completion.completionPhrases[0].tokens);

/**
 * Returns `true` if the completion is from a cohort column
 *   but not the cohort column itself.
 * - A completion can have multiple tokens and so can be both
 *     a cohort column and from a cohort column.
 *
 * E.g.,
 * - `Discount set` is a cohort column.
 * - Completions shown for `Discount set = ` are from a cohort column.
 * - `Discount set = > 12` is both.
 */
export const isCompletionFromACohort = (
    completion: BlinkCompletionOrFoldedCompletion,
) =>
    // TS Bug: Combined interfaces as a type don't pass validation.
    (completion as BlinkCompletion)?.containsCohortToken &&
    isAnyTokenFromACohort(completion.completionPhrases[0].tokens);

export function isQueryEmpty(phrases: SagePhrase[]): boolean {
    if (!phrases) {
        return true;
    }

    return !phrases.some((phrase: SagePhrase) => {
        return phrase.tokens.some((token: SageToken) => {
            return token.text.length > 0;
        });
    });
}

export const areSagePhrasesPresent = (phrases: SagePhrase[] = []): boolean => {
    return (
        Array.isArray(phrases) &&
        Array.isArray(phrases[0]?.tokens) &&
        phrases[0].tokens.length > 0
    );
};

export const convertSagePhrasesToTextTokens = (
    phrases: SagePhrase[] = [],
): string[] => {
    return (
        phrases
            .filter(phrase => phrase.type !== PhraseType.UndefinedPhrase)
            .map(phrase => phrase.tokens.map(token => token.text).join(' '))
            .filter(tokenStr => tokenStr !== '') || []
    );
};

export function countTokensInPhrases(phrases: SagePhrase[]): number {
    return phrases.reduce((count: number, phrase: SagePhrase) => {
        return count + phrase.tokens.length;
    }, 0);
}

/*
 * Given an absolute token index among the flat set of all tokens, convert it into a (phrase index,
 * token index in that phrase) pair. This is useful when converting between a model where tokens
 * are nested within phrase objects, and a model where we have a flat list of all tokens.
 */
export function getPhraseAndTokenIdxForAbsoluteTokenIdx(
    phrases: SagePhrase[],
    targetAbsoluteTokenIdx: number,
) {
    let absoluteTokenIdx = 0;
    for (let phraseIdx = 0; phraseIdx < phrases.length; phraseIdx++) {
        const currentPhrase = phrases[phraseIdx];
        for (
            let tokenIdx = 0;
            tokenIdx < currentPhrase.tokens.length;
            tokenIdx++
        ) {
            if (absoluteTokenIdx === targetAbsoluteTokenIdx) {
                return {
                    phraseIdx,
                    tokenIdx,
                };
            }

            absoluteTokenIdx++;
        }
    }

    logger.error(
        'Target token index is beyond the total number of tokens in all phrases',
        phrases,
        targetAbsoluteTokenIdx,
    );
    return {
        phraseIdx: 0,
        tokenIdx: 0,
    };
}

export const convertTokensToInputTokens = (
    tokens: SageToken[],
): SageTokenInput[] => {
    return tokens.map(
        (token: SageToken): SageTokenInput => ({
            text: token.text,
            hasSpaceAfter: token.hasSpaceAfter,
            trailingWhitespace: token.trailingWhitespace,
            recognizedTokenJson: token.recognizedTokenJson,
        }),
    );
};

/*
 * Mapping from the textual representation of select keywords and operators to a string describing
 * it for metrics purposes. We only need to include any keywords/operators which contain special
 * characters (not just a-z characters). See the usage site for more details.
 */
const KEYWORD_OR_OPERATOR_TEXT_TO_METRICS_STRING: { [key: string]: string } = {
    '=': 'equals',
    '!=': 'notEquals',
    '>': 'greaterThan',
    '>=': 'greaterThanEquals',
    '<': 'lessThan',
    '<=': 'lessThanEquals',
    '(': 'openBracket',
    ')': 'closeBracket',
    '{': 'openBrace',
    '}': 'closeBrace',
    ',': 'comma',
    '/': 'forwardSlash',
    '\\': 'backSlash',
    '*': 'asterisk',
    '+': 'plus',
    '-': 'minus',
    '^': 'caret',
};

/*
 * Constructs a string describing a token for metrics purposes. Includes information about the
 * token type and data type, as well as extra information about operators, keywords, and formula
 * functions if present.
 */
export const getMetricsStructureStringForToken = (token: SageToken): string => {
    let dataTypeString: string;
    if (
        token.type === RecognizedTokenType.Keyword ||
        token.type === RecognizedTokenType.Operator ||
        token.type === RecognizedTokenType.Delimiter
    ) {
        // Keywords and operators don't have a data type, instead just use the text of the keyowrd
        // or operator, but camelCased so we remove any spaces. If it contains special characters,
        // we must handle it ourselves because the loadash function removes any special characters.
        dataTypeString =
            KEYWORD_OR_OPERATOR_TEXT_TO_METRICS_STRING[token.text] ??
            _.camelCase(token.text);
    } else if (token.type === RecognizedTokenType.FunctionName) {
        // Formula functions and delimiters (eg brackets) don't have a data type, instead use the
        // token text itself (should not contain spaces, so no post-processing should be needed)
        dataTypeString = _.camelCase(token.text);
    } else {
        dataTypeString = _.camelCase(token.dataType);
    }

    return `${_.camelCase(token.type)}/${dataTypeString}`;
};

/**
 * Filters a list of data for a source by the given search text
 * This supports multi word partial search, so for example, multi word columns
 * such as "Customer Address", can be searched via the query "Cus Ad"
 */
export const getFilteredResultsBySearchText = (
    options: { searchableName: string; data: any }[],
    searchText = '',
    wordPrefixMatchesOnly = false,
) => {
    // Detects space and special chars in a string, which are used to separate "words"
    const specialCharRegex = new RegExp(/[ ,.\-_]+/);

    return options
        .filter(item => {
            // split search and searchable text, "Cus Ad" -> ["cus", "ad"]
            const searchTextWords = searchText
                .toLowerCase()
                .trim()
                .split(specialCharRegex);
            const itemNameWords = item.searchableName
                .toLowerCase()
                .trim()
                .split(specialCharRegex);

            // if there are more search words than colName words, it's not a match
            if (searchTextWords.length > itemNameWords.length) {
                return false;
            }

            // loop through search words to see if it matches with any of the searchable words
            for (let i = 0; i < searchTextWords.length; i++) {
                const searchTextWord = searchTextWords[i];
                const searchedWordMatchIndex = itemNameWords.findIndex(
                    searchedName =>
                        wordPrefixMatchesOnly
                            ? searchedName.startsWith(searchTextWord)
                            : searchedName.includes(searchTextWord),
                );

                // if the searchable name doesn't match with any of the search text words
                // so for example "Revenue" neither has "Cus" nor "Ad", so it
                // will be filtered out here, and "Supplier Address" has "Ad" but
                // not "Cus", so that will also be filtered out
                if (searchedWordMatchIndex === -1) {
                    return false;
                }

                // itemNameWords got matched so we don't have to consider it for next searchTextWord
                // for example for the search "Cu", and the column "Customer Custkey",
                // Once we we find "Customer" to be matching with "Cu", we don't need to
                // match it agan with "Custkey" because oveall it's already a match.
                itemNameWords.splice(searchedWordMatchIndex, 1);
            }
            return true;
        })
        .map(item => item.data);
};

/**
 * Filters a list of data panel columns for a source by the given search text
 * This supports multi word partial search, so for example, multi word columns
 * such as "Customer Address", can be searched via the query "Cus Ad"
 */
export const getFilteredColumnsBySearchText = (
    columns: Column[] | UnderlyingDataColumn[],
    searchText: string,
    wordPrefixMatchesOnly = false,
) => {
    return getFilteredResultsBySearchText(
        columns.map(column => ({
            searchableName: column.name,
            data: column,
        })),
        searchText,
        wordPrefixMatchesOnly,
    );
};

export const clearSageSearchResponseApolloCache = (
    client: ApolloClient<object>,
    answerSessionId: string,
) => {
    client.cache.evict({
        id: client.cache.identify({
            id: {
                sessionId: answerSessionId,
            },
            __typename: 'SageSearchResponse',
        }),
    });
};

export const clearSageSearchSubject = new Subject();

export const isSageBarLoadingSubject = new Subject();
